import React, { useState } from "react";
import { Table, Badge } from "react-bootstrap";
import QuickAppointmentModal from "./QuickAppointmentModal";
import { useNavigate } from "react-router-dom";
import { FaSyringe, FaPills } from "react-icons/fa";

const RemindersTable = ({ reminders, AuthToken, onRefresh, completionFilter }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedReminder, setSelectedReminder] = useState(null);
    const navigate = useNavigate();


    const role = localStorage.getItem("role") || "";
    const isAdmin = role === "Admin";

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const getTypeIcon = (type) => {
        if (type === 'vaccine') {
            return <FaSyringe className="text-primary me-1" />;
        } else if (type === 'deworming') {
            return <FaPills className="text-success me-1" />;
        }
        return null;
    };

    const getTypeBadge = (type) => {
        if (type === 'vaccine') {
            return <Badge bg="primary">Vaccine</Badge>;
        } else if (type === 'deworming') {
            return <Badge bg="success">Deworming</Badge>;
        }
        return <Badge bg="secondary">{type}</Badge>;
    };

    const handleQuickCreate = (reminder) => {
        setSelectedReminder(reminder);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedReminder(null);
    };

    const handleSuccess = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const processReminders = (remindersData) => {
        if (!remindersData) return [];

        const processed = [];
        const petMap = new Map();

        remindersData.forEach(item => {
            const petId = item.pet_id || item.pet?.id;
            if (!petId) return;

            if (!petMap.has(petId)) {
                petMap.set(petId, {
                    pet_id: petId,
                    appointment_id: item.appointment_id,
                    pet_name: item.pet_name || item.pet?.name || 'N/A',
                    client_name: item.client_name || item.pet?.client?.name || 'N/A',
                    pet_phone: item.pet_phone || item.pet?.phone || 'N/A',
                    reminders: []
                });
            }

            const petData = petMap.get(petId);
            if (item.vaccines) {
                item.vaccines.forEach(vaccine => {
                    petData.reminders.push({
                        ...vaccine,
                        type: 'vaccine',
                        appointment_id: item.appointment_id,
                        service_name: vaccine.vaccine_name,
                        date: vaccine.vaccine_date
                    });
                });
            }
            if (item.dewormings) {
                item.dewormings.forEach(deworming => {
                    petData.reminders.push({
                        ...deworming,
                        type: 'deworming',
                        appointment_id: item.appointment_id,
                        service_name: deworming.deworming_name,
                        date: deworming.deworming_date
                    });
                });
            }
        });

        petMap.forEach((petData) => {
            petData.reminders.sort((a, b) => {
                return new Date(a.date) - new Date(b.date);
            });

            const hasPending = petData.reminders.some(r =>
                r.is_completed === 0 && r.reminder_status !== 'completed'
            );

            if (completionFilter === "dues") {
                if (hasPending) {
                    processed.push(petData);
                }
            } else {
                processed.push(petData);
            }
        });

        return processed;
    };

    const processedReminders = processReminders(reminders);

    return (
        <>
            <div className="table-responsive">
                <Table hover align="middle" className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Client Details</th>
                            <th>Pet Name</th>
                            <th>Service History</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {processedReminders.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center py-4 text-muted">
                                    No reminders found matching the filter criteria.
                                </td>
                            </tr>
                        ) : (
                            processedReminders.map((petData, index) => {
                                const hasPending = petData.reminders.some(r =>
                                    r.is_completed === 0 && r.reminder_status !== 'completed'
                                );

                                return (
                                    <tr key={`${petData.pet_id}-${index}`} className="align-middle">
                                        <td className="fw-bold">{index + 1}</td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getTypeIcon(petData.reminders[0]?.type)}
                                                {getTypeBadge(petData.reminders[0]?.type)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="fw-bold">{petData.client_name || 'N/A'}</div>
                                            {isAdmin &&
                                                <div className="text-muted small">{petData.pet_phone || 'N/A'}</div>
                                            }
                                        </td>
                                        <td>
                                            <div
                                                className="fw-bold text-primary"
                                                style={{ cursor: "pointer", textDecoration: "underline" }}
                                                onClick={() => {
                                                    const firstReminder = petData.reminders[0];

                                                    navigate(`/appointments/${petData.appointment_id}`, {
                                                        state: {
                                                            activeTab:
                                                                firstReminder.type === "vaccine"
                                                                    ? "vaccine"
                                                                    : "deworming",
                                                        },
                                                    });
                                                }}
                                            >
                                                {petData.pet_name || "N/A"}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column gap-1">
                                                {petData.reminders.map((reminder, idx) => {
                                                    const isCompleted =
                                                        reminder.is_completed === 1 ||
                                                        reminder.reminder_status === "completed";

                                                    const displayName =
                                                        reminder.service_name ||
                                                        reminder.vaccine_name ||
                                                        reminder.deworming_name ||
                                                        "N/A";

                                                    const customBgColor = isCompleted ? "#109c1a" : "#ffb700";
                                                    const textColor = "#ffffff";

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="d-flex align-items-center justify-content-between px-2 py-1 rounded"
                                                            style={{
                                                                backgroundColor: customBgColor,
                                                                color: textColor,
                                                                fontSize: "0.8rem",
                                                                fontWeight: "500",
                                                                width: "fit-content",
                                                                minWidth: "220px",
                                                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                <span
                                                                    style={{
                                                                        fontSize: "0.75rem",
                                                                        fontWeight: "600",
                                                                        opacity: 0.95,
                                                                    }}
                                                                >
                                                                    Shot {idx + 1}
                                                                </span>

                                                                <span
                                                                    className="fw-bold"
                                                                    style={{ letterSpacing: "0.5px" }}
                                                                >
                                                                    {formatDate(reminder.date || reminder.reminder_date)}
                                                                </span>

                                                                <span
                                                                    style={{
                                                                        fontSize: "0.75rem",
                                                                        fontWeight: "400",
                                                                        opacity: 0.95,
                                                                    }}
                                                                >
                                                                    {displayName}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center justify-content-start h-100">
                                                {hasPending && (
                                                    <button
                                                        onClick={() => {
                                                            const pendingReminder = petData.reminders.find(r =>
                                                                r.is_completed === 0 && r.reminder_status !== 'completed'
                                                            );
                                                            handleQuickCreate({
                                                                ...pendingReminder,
                                                                pet_name: petData.pet_name,
                                                                client_name: petData.client_name,
                                                                pet_phone: petData.pet_phone,
                                                                pet_id: petData.pet_id
                                                            });
                                                        }}
                                                        title="Complete Record"
                                                        className="btn btn-sm text-white"
                                                        style={{
                                                            backgroundColor: '#00c5fb',
                                                            border: 'none',

                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        Update Record
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>
            </div>

            <QuickAppointmentModal
                show={showModal}
                onHide={handleModalClose}
                reminder={selectedReminder}
                AuthToken={AuthToken}
                onSuccess={handleSuccess}
            />
        </>
    );
};

export default RemindersTable;